/**
 * globalDocTemplates - the ONE real template catalogue for the
 * Global Document Editor and the Document Template Gallery.
 *
 * Before 2026-11-05 the gallery generated a fake 112-template
 * catalogue with Math.random() download counts and ratings plus
 * "OFFICIAL" badges, while the editor itself held the only real
 * templates (5) in a private constant. Both surfaces now read from
 * this module, so every count shown anywhere in the product is the
 * real number of templates that actually open in the editor.
 */

export type FieldType = 'text' | 'number' | 'date' | 'photo' | 'signature' | 'ssn' | 'ein' | 'currency' | 'select';

export interface EditableField {
    id: string;
    name: string;
    label: string;
    type: FieldType;
    value: string;
    required: boolean;
    options?: string[];
    position: { x: number; y: number; width: number; height: number };
    style?: { fontSize?: number; fontWeight?: string; color?: string; textTransform?: string };
    /**
     * Signature fields only (parity pass, 2026-11-05): the RAW
     * signature source (the upload / pad drawing as it arrived,
     * before ink derivation). The placed `value` is derived from
     * it under the editor's signature ink, so an ink pick can
     * re-derive the placed signature exactly - the model the
     * Check Studio shipped. Storage strips drop it together with
     * the value.
     */
    rawValue?: string;
    /** Typed-signature provenance: a typed signature re-renders
     * from its text at any ink instead of tinting a raster. */
    sigSource?: { kind: 'typed'; text: string; face: string; ink: string };
}

/**
 * An object placed onto a template document from the shared
 * asset libraries (parity pass, 2026-11-05): a logo, signature
 * or picture kept in the library and tapped into the document.
 * It is an ordinary editor object - movable, scalable,
 * deletable - positioned in layout units like the fields, and
 * it renders in the preview and the export alike. `src` is the
 * image as placed (for a signature, already ink-derived);
 * assetId/assetKind record which library entry it came from.
 */
export interface PlacedDocItem {
    id: string;
    kind: 'image' | 'text';
    src?: string;
    text?: string;
    assetId?: string;
    assetKind?: 'logos' | 'signatures' | 'pictures';
    position: { x: number; y: number; width: number; height: number };
}

export interface DocumentData {
    id: string;
    name: string;
    category: string;
    country: string;
    region?: string;
    fields: EditableField[];
    layout: { width: number; height: number; orientation: string; backgroundColor: string };
}

export const GLOBAL_DOC_TEMPLATES: Record<string, DocumentData> = {
    us_passport: {
        id: 'us_passport', name: 'US Passport', category: 'passport', country: 'United States',
        fields: [
            { id: 'f1', name: 'surname', label: 'Surname', type: 'text', value: '', required: true, position: { x: 140, y: 50, width: 180, height: 22 }, style: { textTransform: 'uppercase', fontWeight: 'bold' } },
            { id: 'f2', name: 'givenNames', label: 'Given Names', type: 'text', value: '', required: true, position: { x: 140, y: 80, width: 180, height: 22 }, style: { textTransform: 'uppercase' } },
            { id: 'f3', name: 'passportNo', label: 'Passport No.', type: 'text', value: '', required: true, position: { x: 140, y: 110, width: 120, height: 22 } },
            { id: 'f4', name: 'nationality', label: 'Nationality', type: 'text', value: 'UNITED STATES', required: true, position: { x: 140, y: 140, width: 150, height: 22 } },
            { id: 'f5', name: 'dob', label: 'Date of Birth', type: 'date', value: '', required: true, position: { x: 140, y: 170, width: 100, height: 22 } },
            { id: 'f6', name: 'sex', label: 'Sex', type: 'select', value: 'M', required: true, options: ['M', 'F', 'X'], position: { x: 260, y: 170, width: 50, height: 22 } },
            { id: 'f7', name: 'pob', label: 'Place of Birth', type: 'text', value: '', required: true, position: { x: 140, y: 200, width: 150, height: 22 } },
            { id: 'f8', name: 'issue', label: 'Date of Issue', type: 'date', value: '', required: true, position: { x: 140, y: 230, width: 100, height: 22 } },
            { id: 'f9', name: 'expiry', label: 'Date of Expiry', type: 'date', value: '', required: true, position: { x: 140, y: 260, width: 100, height: 22 } },
            { id: 'f10', name: 'photo', label: 'Photo', type: 'photo', value: '', required: true, position: { x: 15, y: 50, width: 110, height: 140 } },
            { id: 'f11', name: 'signature', label: 'Signature', type: 'signature', value: '', required: true, position: { x: 15, y: 200, width: 110, height: 40 } }
        ],
        layout: { width: 350, height: 380, orientation: 'portrait', backgroundColor: '#1a3a5c' }
    },
    ssn_card: {
        id: 'ssn_card', name: 'SSN Card', category: 'ssn_card', country: 'United States',
        fields: [
            { id: 'f1', name: 'name', label: 'Full Name', type: 'text', value: '', required: true, position: { x: 60, y: 70, width: 200, height: 28 }, style: { fontSize: 18, fontWeight: 'bold', textTransform: 'uppercase' } },
            { id: 'f2', name: 'ssn', label: 'SSN', type: 'ssn', value: '', required: true, position: { x: 60, y: 110, width: 180, height: 32 }, style: { fontSize: 22, fontWeight: 'bold' } },
            { id: 'f3', name: 'signature', label: 'Signature', type: 'signature', value: '', required: true, position: { x: 60, y: 150, width: 150, height: 30 } }
        ],
        layout: { width: 320, height: 200, orientation: 'landscape', backgroundColor: '#E8E4D9' }
    },
    california_dl: {
        id: 'california_dl', name: 'California DL', category: 'drivers_license', country: 'United States', region: 'California',
        fields: [
            { id: 'f1', name: 'dlNumber', label: 'DL Number', type: 'text', value: '', required: true, position: { x: 130, y: 25, width: 140, height: 20 }, style: { fontWeight: 'bold' } },
            { id: 'f2', name: 'class', label: 'Class', type: 'select', value: 'C', required: true, options: ['A', 'B', 'C', 'M'], position: { x: 280, y: 25, width: 40, height: 20 } },
            { id: 'f3', name: 'lastName', label: 'Last Name', type: 'text', value: '', required: true, position: { x: 130, y: 50, width: 180, height: 18 }, style: { textTransform: 'uppercase', fontWeight: 'bold' } },
            { id: 'f4', name: 'firstName', label: 'First Name', type: 'text', value: '', required: true, position: { x: 130, y: 72, width: 120, height: 18 }, style: { textTransform: 'uppercase' } },
            { id: 'f5', name: 'address', label: 'Address', type: 'text', value: '', required: true, position: { x: 130, y: 94, width: 190, height: 32 } },
            { id: 'f6', name: 'dob', label: 'DOB', type: 'date', value: '', required: true, position: { x: 130, y: 132, width: 80, height: 18 } },
            { id: 'f7', name: 'sex', label: 'Sex', type: 'select', value: 'M', required: true, options: ['M', 'F', 'X'], position: { x: 220, y: 132, width: 30, height: 18 } },
            { id: 'f8', name: 'height', label: 'HGT', type: 'text', value: '', required: true, position: { x: 260, y: 132, width: 40, height: 18 } },
            { id: 'f9', name: 'eyes', label: 'Eyes', type: 'select', value: 'BRO', required: true, options: ['BLK', 'BLU', 'BRO', 'GRN', 'GRY', 'HAZ'], position: { x: 130, y: 154, width: 45, height: 18 } },
            { id: 'f10', name: 'exp', label: 'EXP', type: 'date', value: '', required: true, position: { x: 200, y: 154, width: 70, height: 18 } },
            { id: 'f11', name: 'photo', label: 'Photo', type: 'photo', value: '', required: true, position: { x: 10, y: 25, width: 105, height: 130 } },
            { id: 'f12', name: 'signature', label: 'Signature', type: 'signature', value: '', required: true, position: { x: 10, y: 160, width: 105, height: 30 } }
        ],
        layout: { width: 340, height: 210, orientation: 'landscape', backgroundColor: '#FFFFFF' }
    },
    uk_passport: {
        id: 'uk_passport', name: 'UK Passport', category: 'passport', country: 'United Kingdom',
        fields: [
            { id: 'f1', name: 'surname', label: 'Surname', type: 'text', value: '', required: true, position: { x: 140, y: 50, width: 180, height: 22 }, style: { textTransform: 'uppercase', fontWeight: 'bold' } },
            { id: 'f2', name: 'givenNames', label: 'Given Names', type: 'text', value: '', required: true, position: { x: 140, y: 80, width: 180, height: 22 } },
            { id: 'f3', name: 'passportNo', label: 'Passport No.', type: 'text', value: '', required: true, position: { x: 140, y: 110, width: 120, height: 22 } },
            { id: 'f4', name: 'nationality', label: 'Nationality', type: 'text', value: 'BRITISH CITIZEN', required: true, position: { x: 140, y: 140, width: 150, height: 22 } },
            { id: 'f5', name: 'dob', label: 'Date of Birth', type: 'date', value: '', required: true, position: { x: 140, y: 170, width: 100, height: 22 } },
            { id: 'f6', name: 'sex', label: 'Sex', type: 'select', value: 'M', required: true, options: ['M', 'F', 'X'], position: { x: 260, y: 170, width: 50, height: 22 } },
            { id: 'f7', name: 'pob', label: 'Place of Birth', type: 'text', value: '', required: true, position: { x: 140, y: 200, width: 150, height: 22 } },
            { id: 'f8', name: 'issue', label: 'Date of Issue', type: 'date', value: '', required: true, position: { x: 140, y: 230, width: 100, height: 22 } },
            { id: 'f9', name: 'expiry', label: 'Date of Expiry', type: 'date', value: '', required: true, position: { x: 140, y: 260, width: 100, height: 22 } },
            { id: 'f10', name: 'photo', label: 'Photo', type: 'photo', value: '', required: true, position: { x: 15, y: 50, width: 110, height: 140 } },
            { id: 'f11', name: 'signature', label: 'Signature', type: 'signature', value: '', required: true, position: { x: 15, y: 200, width: 110, height: 40 } }
        ],
        layout: { width: 350, height: 380, orientation: 'portrait', backgroundColor: '#8a1a34' }
    },
    german_id: {
        id: 'german_id', name: 'German ID', category: 'national_id', country: 'Germany',
        fields: [
            { id: 'f1', name: 'surname', label: 'Familienname', type: 'text', value: '', required: true, position: { x: 130, y: 35, width: 150, height: 20 }, style: { textTransform: 'uppercase', fontWeight: 'bold' } },
            { id: 'f2', name: 'givenNames', label: 'Vornamen', type: 'text', value: '', required: true, position: { x: 130, y: 58, width: 150, height: 20 } },
            { id: 'f3', name: 'idNumber', label: 'Ausweisnummer', type: 'text', value: '', required: true, position: { x: 130, y: 81, width: 120, height: 20 } },
            { id: 'f4', name: 'nationality', label: 'Staatsangehörigkeit', type: 'text', value: 'DEUTSCH', required: true, position: { x: 130, y: 104, width: 100, height: 20 } },
            { id: 'f5', name: 'dob', label: 'Geburtsdatum', type: 'date', value: '', required: true, position: { x: 130, y: 127, width: 90, height: 20 } },
            { id: 'f6', name: 'expiry', label: 'Gültig bis', type: 'date', value: '', required: true, position: { x: 130, y: 150, width: 90, height: 20 } },
            { id: 'f7', name: 'photo', label: 'Foto', type: 'photo', value: '', required: true, position: { x: 10, y: 30, width: 105, height: 130 } },
            { id: 'f8', name: 'signature', label: 'Unterschrift', type: 'signature', value: '', required: true, position: { x: 130, y: 173, width: 100, height: 25 } }
        ],
        layout: { width: 320, height: 210, orientation: 'landscape', backgroundColor: '#D4E7F0' }
    }
};

/** A fresh, unshared copy of a stock template (templates are edited in place by the editor state). */
export function freshTemplate(id: string): DocumentData | null {
    const t = GLOBAL_DOC_TEMPLATES[id];
    if (!t) return null;
    return JSON.parse(JSON.stringify(t)) as DocumentData;
}

export interface GlobalDocTemplateMeta {
    id: string;
    name: string;
    category: string;
    country: string;
    countryCode: string;
    flag: string;
    region?: string;
}

const COUNTRY_CODES: Record<string, { code: string; flag: string }> = {
    'United States': { code: 'US', flag: '🇺🇸' },
    'United Kingdom': { code: 'GB', flag: '🇬🇧' },
    'Germany': { code: 'DE', flag: '🇩🇪' }
};

/** Gallery metadata derived from the real templates — never hand-maintained separately. */
export const GLOBAL_DOC_TEMPLATE_LIST: GlobalDocTemplateMeta[] = Object.values(GLOBAL_DOC_TEMPLATES).map(t => ({
    id: t.id,
    name: t.name,
    category: t.category,
    country: t.country,
    countryCode: COUNTRY_CODES[t.country]?.code || '',
    flag: COUNTRY_CODES[t.country]?.flag || '🌍',
    region: t.region
}));

export const GLOBAL_DOC_CATEGORIES: { id: string; name: string; icon: string }[] = [
    { id: 'passport', name: 'Passports', icon: '🛂' },
    { id: 'drivers_license', name: "Driver's Licenses", icon: '🪪' },
    { id: 'national_id', name: 'National IDs', icon: '🆔' },
    { id: 'ssn_card', name: 'SSN / Tax IDs', icon: '📋' }
];

/**
 * Map a protected clone engine result into an editor document.
 * This is the exact mapping the editor has always applied at the
 * engine boundary (field types forced to text, coordinates halved);
 * it lives here so the gallery's upload flow builds the identical
 * document the editor's own clone button builds.
 */
export function mapClonedDocument(cloned: any): DocumentData {
    const mappedFields: EditableField[] = (cloned.editableFields || []).map((f: any) => ({
        id: f.id,
        name: f.name,
        label: f.name.charAt(0).toUpperCase() + f.name.slice(1).replace('_', ' '),
        type: 'text' as FieldType,
        value: f.value,
        required: true,
        position: { x: f.rect.x / 2, y: f.rect.y / 2, width: f.rect.width / 2, height: f.rect.height / 2 }
    }));
    return {
        id: cloned.id,
        name: `Cloned ${cloned.type}`,
        category: cloned.type,
        country: 'Unknown',
        fields: mappedFields,
        layout: {
            width: cloned.details.layout.width / 2,
            height: cloned.details.layout.height / 2,
            orientation: 'portrait',
            backgroundColor: '#FFFFFF'
        }
    };
}

/** Default border for a brand-new (e.g. cloned/uploaded) document design. */
export const DEFAULT_DOC_BORDER = { style: 'SOLID' as const, color: '#000000', thickness: 2, cornerRadius: 0 };
